import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/modal";

/* ─── Styled Components ─────────────────────────────────────────── */

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: ${({ theme }) => theme.blueish};
    font-family: "SatoshiBold";
    font-size: 14px;
    text-align: left;
  }
`;

const Input = styled.input`
  height: 40px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.lines};
  color: ${({ theme }) => theme.white};
  padding: 0 12px;
  font-size: 14px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.red};
  font-size: 13px;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${({ $variant, theme }) => ($variant === "primary" ? theme.green : theme.borderColor)};
  background: ${({ $variant, theme }) => ($variant === "primary" ? theme.green : "transparent")};
  color: ${({ $variant, theme }) => ($variant === "primary" ? theme.black : theme.blueish)};
  border-radius: 4px;
  padding: 10px 18px;
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 14px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* ─── Formation Picker ───────────────────────────────────────────── */

const PickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  padding: 12px;
  background: ${({ theme }) => theme.lines};

  /* scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.borderColor};
    border-radius: 2px;
  }
`;

const CategoryLabel = styled.p`
  font-family: "SatoshiBold";
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.blueish};
  opacity: 0.6;
  margin: 4px 0 6px;
`;

const CategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & + & {
    margin-top: 8px;
  }
`;

const FormationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const FormationCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 4px;
  border-radius: 6px;
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.green : theme.borderColor)};
  background: ${({ $selected, theme }) => ($selected ? "rgba(163,230,53,0.08)" : "transparent")};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ $selected, theme }) => ($selected ? theme.green : "rgba(163,230,53,0.4)")};
    background: rgba(163, 230, 53, 0.05);
  }
`;

const FormationName = styled.span`
  font-family: "SatoshiBold";
  font-size: 10px;
  color: ${({ $selected, theme }) => ($selected ? theme.green : theme.white)};
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
`;

/* ─── Mini Pitch SVG ─────────────────────────────────────────────── */

const PITCH_W = 52;
const PITCH_H = 68;
const PITCH_PAD = 5;
const DOT_R = 2.8;

const MiniPitch = ({ lines = [], isSelected = false }) => {
  // All rows bottom-to-top: GK first, then lines[0..n-1]
  const allRows = [1, ...lines];
  const totalRows = allRows.length;

  const dots = [];

  allRows.forEach((count, rowIndex) => {
    // rowIndex 0 = GK → bottom of pitch (large y in SVG)
    // rowIndex totalRows-1 = attackers → top of pitch (small y in SVG)
    const yFraction = rowIndex / Math.max(totalRows - 1, 1);
    const y = PITCH_H - PITCH_PAD - yFraction * (PITCH_H - PITCH_PAD * 2.4);

    for (let i = 0; i < count; i++) {
      const x = PITCH_PAD + ((i + 1) / (count + 1)) * (PITCH_W - PITCH_PAD * 2);
      dots.push({ x, y, isGK: rowIndex === 0 });
    }
  });

  return (
    <svg
      width={PITCH_W}
      height={PITCH_H}
      viewBox={`0 0 ${PITCH_W} ${PITCH_H}`}
      style={{ display: "block", flexShrink: 0 }}
    >
      {/* Pitch background */}
      <rect x={0} y={0} width={PITCH_W} height={PITCH_H} rx={3} fill="#1a3d10" />

      {/* Pitch markings */}
      {/* Outline */}
      <rect
        x={PITCH_PAD - 1}
        y={PITCH_PAD - 1}
        width={PITCH_W - (PITCH_PAD - 1) * 2}
        height={PITCH_H - (PITCH_PAD - 1) * 2}
        rx={1}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={0.6}
      />
      {/* Centre line */}
      <line
        x1={PITCH_PAD}
        y1={PITCH_H / 2}
        x2={PITCH_W - PITCH_PAD}
        y2={PITCH_H / 2}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={0.5}
      />
      {/* Centre circle (mini) */}
      <circle
        cx={PITCH_W / 2}
        cy={PITCH_H / 2}
        r={5}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={0.5}
      />
      {/* Top penalty area */}
      <rect
        x={PITCH_W / 2 - 10}
        y={PITCH_PAD - 1}
        width={20}
        height={10}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={0.5}
      />
      {/* Bottom penalty area */}
      <rect
        x={PITCH_W / 2 - 10}
        y={PITCH_H - PITCH_PAD + 1 - 10}
        width={20}
        height={10}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={0.5}
      />

      {/* Player dots */}
      {dots.map((dot, idx) => (
        <circle
          key={idx}
          cx={dot.x}
          cy={dot.y}
          r={DOT_R}
          fill={dot.isGK ? "#f5c518" : isSelected ? "#a3e635" : "#86c96b"}
        />
      ))}
    </svg>
  );
};

/* ─── Category display names ─────────────────────────────────────── */

const CATEGORY_DISPLAY = {
  back_three: "Back Three",
  back_four: "Back Four",
  back_five: "Back Five",
  balanced: "Balanced",
  attacking: "Attacking",
  defensive: "Defensive",
  wide: "Wide Play",
  narrow: "Narrow",
};

const getCategoryLabel = (category) => {
  if (!category) return "Other";
  return CATEGORY_DISPLAY[category] ?? category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/* ─── Formation Picker Component ─────────────────────────────────── */

const FormationPicker = ({ formations, selectedId, onSelect, disabled }) => {
  // Group formations by category, preserving insertion order
  const groups = useMemo(() => {
    const map = new Map();

    formations.forEach((formation) => {
      const key = formation.category ?? "__other__";
      if (!map.has(key)) {
        map.set(key, { category: formation.category, formations: [] });
      }
      map.get(key).formations.push(formation);
    });

    return [...map.values()];
  }, [formations]);

  if (!formations.length) return null;

  return (
    <PickerWrapper>
      {groups.map((group) => (
        <CategoryGroup key={group.category ?? "__other__"}>
          <CategoryLabel>{getCategoryLabel(group.category)}</CategoryLabel>
          <FormationGrid>
            {group.formations.map((formation) => {
              const selected = formation.id === selectedId;
              return (
                <FormationCard
                  key={formation.id}
                  type="button"
                  $selected={selected}
                  disabled={disabled}
                  onClick={() => onSelect(formation.id)}
                  title={formation.name}
                >
                  <MiniPitch lines={formation.lines ?? []} isSelected={selected} />
                  <FormationName $selected={selected}>{formation.name}</FormationName>
                </FormationCard>
              );
            })}
          </FormationGrid>
        </CategoryGroup>
      ))}
    </PickerWrapper>
  );
};

/* ─── Main Modal ─────────────────────────────────────────────────── */

const ShadowTeamFormModal = ({
  isOpen,
  mode = "create",
  formations = [],
  initialName = "",
  initialFormationId = null,
  isLoading = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const formationOptions = useMemo(
    () => formations.filter((formation) => formation?.id !== null && formation?.id !== undefined),
    [formations]
  );

  const defaultFormationId = formationOptions[0]?.id ?? "";

  const [name, setName] = useState("");
  const [formationId, setFormationId] = useState(defaultFormationId);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const resolvedFormationId =
      initialFormationId !== null && initialFormationId !== undefined
        ? Number(initialFormationId)
        : defaultFormationId;

    setName(initialName || "");
    setFormationId(resolvedFormationId);
    setError("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if ((formationId === "" || formationId === null || formationId === undefined) && formationOptions.length) {
      setFormationId(formationOptions[0].id);
    }
  }, [formationId, formationOptions, isOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name.trim()) {
      setError(t("shadowTeam.teamNamePlaceholder"));
      return;
    }

    const resolvedFormationId =
      formationId === "" || formationId === null || formationId === undefined
        ? formationOptions[0]?.id ?? null
        : Number(formationId);

    if (resolvedFormationId === null) {
      setError(t("shadowTeam.formation"));
      return;
    }

    setError("");

    await onSubmit?.({
      name: name.trim(),
      formationId: resolvedFormationId,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      closeIc
      padding="withClose"
      width="600px"
      unstyledTypography
      contentWidth="90%"
    >
      <h1 style={{ marginBottom: 20, textAlign: "left" }}>
        {mode === "edit" ? t("shadowTeam.editTeam") : t("shadowTeam.createTeam")}
      </h1>

      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <span>{t("shadowTeam.teamName")}</span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("shadowTeam.teamNamePlaceholder")}
            maxLength={255}
          />
        </FieldGroup>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span
            style={{
              color: "var(--blueish, #8fa3b0)",
              fontFamily: "SatoshiBold",
              fontSize: 14,
              textAlign: "left",
            }}
          >
            {t("shadowTeam.formation")}
          </span>
          <FormationPicker
            formations={formationOptions}
            selectedId={Number(formationId)}
            onSelect={(id) => setFormationId(id)}
            disabled={isLoading}
          />
        </div>

        {error ? <ErrorText>{error}</ErrorText> : null}

        <Actions>
          <ActionButton type="button" onClick={onClose} disabled={isLoading}>
            {t("shadowTeam.cancel")}
          </ActionButton>
          <ActionButton type="submit" $variant="primary" disabled={isLoading || !formationOptions.length}>
            {isLoading ? t("shadowTeam.searching") : t("shadowTeam.save")}
          </ActionButton>
        </Actions>
      </Form>
    </Modal>
  );
};

export default ShadowTeamFormModal;
